/* ============ keeps Shopify's cart in step with the mockup's bag ============
   The bag page renders Shopify's real cart (see the oso-bag section), so Shopify
   is the single source of truth for money. This file's job is to mirror every
   change made through the mockup's own UI — the drawer, the quick-view, the PDP,
   the build-a-set page — into that cart the moment it happens.

   Mirrored: add, quantity change, remove, clear. Anything that isn't mirrored
   would let the drawer quote a total the checkout won't honour, which is exactly
   the bug this replaces. */
(function () {
  if (!window.OSO_STORE) return;
  const S = OSO_STORE;

  /* mockup item -> Shopify variant id, via the table oso-variants.liquid emits */
  function variantId(id, color, size, cn) {
    const map = (window.OSO_SHOPIFY || {})[id];
    if (!map) return null;
    const name = window.OSO_colorName ? OSO_colorName(color) : cn;
    return map[name + '|' + size] || map[(cn || '') + '|' + size] || null;
  }

  /* the store keys cart lines "id|color|size" — resolve one back to a variant */
  function variantForKey(k) {
    const it = S.getCart().find(i => S.keyOf(i) === k);
    return it ? variantId(it.id, it.color, it.size, it.cn) : null;
  }

  /* Every write goes through one promise chain. Concurrent writes are unsafe on a
     cart that doesn't exist yet: each request creates its own cart and the last
     Set-Cookie wins, so a burst of adds (build-a-set sends three at once) silently
     keeps only one of them. Serialised, the first request creates the cart and the
     rest join it. */
  let chain = Promise.resolve();
  function post(url, body) {
    const run = () => fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(r => {
      if (!r.ok) return r.text().then(t => { throw new Error(url + ' -> ' + r.status + ' ' + t.slice(0, 200)); });
      return r.json();
    });
    const next = chain.then(run, run);
    chain = next.catch(() => {});          // one failure must not stall the queue
    return next;
  }

  /* refresh the header badge from Shopify so it reflects the real cart */
  function syncBadge() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(r => r.json())
      .then(c => {
        window.OSO_SHOPIFY_COUNT = c.item_count;
        document.querySelectorAll('.oso-badge').forEach(b => {
          if (b.closest('a') && /wishlist/.test(b.closest('a').getAttribute('href') || '')) return;
          b.textContent = c.item_count;
          b.style.display = c.item_count ? 'flex' : 'none';
        });
      })
      .catch(() => { /* the badge is cosmetic — never block on it */ });
  }

  const warn = (what, a, b) => console.warn('[oso] no Shopify variant to ' + what, a, b);

  /* ---- add ---- coalesced. Build-a-set calls addToCart three times in a row and
     then navigates ~900ms later; three round trips don't reliably finish in time,
     one does. Adds made in the same tick become a single /cart/add.js request. */
  let pending = null;
  const _add = S.addToCart.bind(S);
  S.addToCart = function (id, color, cn, size, qty) {
    _add(id, color, cn, size, qty);
    const vid = variantId(id, color, size, cn);
    if (!vid) return warn('add', id, cn + '|' + size);
    if (!pending) {
      pending = [];
      Promise.resolve().then(() => {
        const items = pending; pending = null;
        post('/cart/add.js', { items: items })
          .then(syncBadge).catch(e => console.error('[oso] add failed', e));
      });
    }
    pending.push({ id: vid, quantity: qty || 1 });
  };

  /* ---- quantity ---- resolve the variant BEFORE the local change, then set
     the absolute quantity on Shopify's line so the two can't drift */
  const _setQty = S.setQty.bind(S);
  S.setQty = function (k, qty) {
    const vid = variantForKey(k);
    _setQty(k, qty);
    if (!vid) return warn('update', k);
    post('/cart/change.js', { id: String(vid), quantity: Math.max(1, qty | 0) })
      .then(syncBadge).catch(e => console.error('[oso] qty change failed', e));
  };

  /* ---- remove ---- */
  const _remove = S.removeFromCart.bind(S);
  S.removeFromCart = function (k) {
    const vid = variantForKey(k);
    _remove(k);
    if (!vid) return warn('remove', k);
    post('/cart/change.js', { id: String(vid), quantity: 0 })
      .then(syncBadge).catch(e => console.error('[oso] remove failed', e));
  };

  /* ---- clear ---- */
  const _clear = S.clearCart.bind(S);
  S.clearCart = function () {
    _clear();
    post('/cart/clear.js', {}).then(syncBadge).catch(() => {});
  };

  /* the drawer's checkout button goes to the real bag; from there the native
     form submit is what reaches Shopify's hosted checkout */
  document.addEventListener('click', function (e) {
    const t = e.target.closest && e.target.closest('.dr-checkout, [data-checkout]');
    if (t && !t.closest('form')) { e.preventDefault(); location.href = '/cart'; }
  }, true);

  /* pages that navigate straight after adding (build-a-set) await this, so the
     cart is written before the browser tears the page down */
  window.osoCartSynced = () => Promise.resolve()
    .then(() => new Promise(r => setTimeout(r, 0)))   // let a same-tick batch flush
    .then(() => chain);

  /* ---- reconcile on load: Shopify is the only truth ----
     The drawer draws from localStorage, the bag page renders Shopify's real cart.
     localStorage never expires; Shopify's cart cookie does — and is dropped by
     private browsing, storage clearing and iOS tracking prevention. When that
     happened the shopper kept seeing items in the drawer while the bag page said
     "Your bag is having a nap", and they could not check out. A phantom cart is
     worse than an empty one, so on every load we rewrite the local cart to match
     what Shopify actually holds. */
  function reconcile() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(r => r.json())
      .then(c => {
        /* variant id -> the mockup's (product, colour, size), the reverse of the
           table oso-variants.liquid emits */
        const back = {};
        const map = window.OSO_SHOPIFY || {};
        for (const pid of Object.keys(map))
          for (const k of Object.keys(map[pid])) {
            const [cn, size] = k.split('|');
            back[map[pid][k]] = { id: pid, cn, size };
          }

        const rebuilt = [];
        for (const line of c.items) {
          const m = back[line.id];
          if (!m) continue;                       // a variant we cannot map back
          const hex = (window.OSO_COLORS || []).reduce((a, x) => x[1] === m.cn ? x[0] : a, null);
          if (!hex) continue;
          rebuilt.push({ id: m.id, color: hex, cn: m.cn, size: m.size, qty: line.quantity });
        }

        const local = S.getCart();
        const same = local.length === rebuilt.length &&
          rebuilt.every(r => local.some(l => S.keyOf(l) === S.keyOf(r) && l.qty === r.qty));
        if (same) return;

        /* write it straight to storage — going through addToCart would mirror
           each line back to Shopify and start a loop */
        try {
          localStorage.setItem('oso_cart_v1', JSON.stringify(rebuilt));
          window.dispatchEvent(new CustomEvent('oso:store'));
        } catch (e) { /* storage full or blocked: leave the cart alone */ }
      })
      .catch(() => { /* offline: better to keep showing the local cart than wipe it */ });
  }

  reconcile().then(syncBadge);
  window.osoReconcileCart = reconcile;

  syncBadge();
  window.osoSyncBadge = syncBadge;
})();
